import React, { Fragment, useEffect, useState } from "react";
import { MdCheckCircle, MdCheck } from "react-icons/md";
import { Link, useLocation } from "react-router-dom";
import { subscriptionConfirmation } from "../../Services/SubscriptionService";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import DotLottie from "./DotLottie";

const formatCurrency = (amount, currency) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency?.toUpperCase() || "USD",
    }).format(amount ?? 0);
  } catch {
    return `${currency?.toUpperCase() || "USD"} ${amount ?? 0}`;
  }
};

const getBillingLabel = (billingMethod) => {
  const normalizedMethod = billingMethod?.toLowerCase();
  return normalizedMethod === "yearly" || normalizedMethod === "annual"
    ? "Yearly"
    : "Monthly";
};

const SubscriptionSuccess = () => {

  const {id} = useParams();


  const [countdown, setCountdown] = useState(6);
  
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    
    const token = localStorage.getItem("accesstoken") || sessionStorage.getItem("accesstoken");


    const loadSubscriptionConfirmation = async () => {
      try {
        const response = await subscriptionConfirmation(token, id);


        if (response?.is_success === true) {
          setSubscriptionData(response?.data ?? response);
        } else {
          const message = response?.status_code === 500
            ? "Internal Server Error. Please contact the support team."
            : response?.message || "The subscription confirmation was not successful.";

          setError(message);
          toast.error(message);
        }
      } catch (error) {
        const message = "Unable to connect to the subscription confirmation service.";
        setError(message);
        toast.error(message);
        console.error("Subscription Confirmation error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptionConfirmation();


  }, [id]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);


  if (isLoading) {
    return <div className="d-flex justify-content-center align-items-center min-vh-100">
        <DotLottie />
      </div>
  }

  if (error || !subscriptionData) {
    return <div className="min-vh-100 d-flex flex-column justify-content-center align-items-center gap-3 p-3 text-center">
      <p className="text-danger mb-0">{error || "Subscription details are unavailable."}</p>
      <Link to="/" className="btn btn-outline-secondary rounded-pill btn-sm px-3 fw-semibold">Back to Login</Link>
    </div>;
  }

  const { organization_name: organizationName, plan = {}, price = {} } = subscriptionData;
  const billingLabel = getBillingLabel(price.billing_method);
  const renewalDate = new Date();
  renewalDate.setMonth(renewalDate.getMonth() + (billingLabel === "Yearly" ? 12 : 1));
  const formattedRenewalDate = renewalDate.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const formattedAmount = formatCurrency(price.amount, price.currency);


  return (
    <Fragment>
      <div className="bg-secondary bg-opacity-25 min-vh-100 d-flex justify-content-center align-items-center p-3" style={{background: "radial-gradient(circle at 70% 20%, #0e7a8063 0%, var(--hc-surface) 100%)"}}>
      <div 
        className="card border-0 rounded-5 shadow-lg overflow-hidden w-100" 
        style={{ maxWidth: '480px' }}
      >

        <div 
          className="w-100" style={{ 
            height: '4px', 
            background: 'linear-gradient(90deg, #198754 50%, #ffc107 50%)' 
          }}
        />

        <div className="card-body p-4 text-center">
          

          <div className="d-flex justify-content-center mb-3">
            <div 
              className="rounded-circle border border-success border-3 d-flex align-items-center justify-content-center text-success"
              style={{ width: '70px', height: '70px', fontSize: '4rem' }}
            >
              <MdCheckCircle/>
            </div>
          </div>


          <div className="d-flex align-items-center justify-content-center gap-1 mb-2">
            <span className="bg-success rounded-circle" style={{ width: '8px', height: '8px' }}></span>
            <span className="text-success fw-bold text-uppercase small" style={{ letterSpacing: '1px', fontSize: '0.75rem' }}>
              Payment Received
            </span>
          </div>

    
          <h2 className="fw-bold text-dark mb-2" style={{ fontFamily: 'serif' }}>
            {organizationName || "Your subscription is active"}
          </h2>

          <p className="text-muted small mb-4 px-3">
            Your subscription is active now. Your {plan.name || "plan"} is ready to use.
          </p>

  
          <div className="bg-light rounded-4 p-4 border text-start mb-4">
            
            
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <span className="badge bg-warning bg-opacity-25 text-warning-emphasis rounded-pill px-2 py-1 mb-2  text-uppercase fw-semibold" style={{ fontSize: '0.65rem'}}>
                  Active Plan
                </span>
                <h4 className="fw-bold mb-0 text-dark">{plan.name || "Subscription plan"}</h4>
                <small className="text-muted">{billingLabel} · renews {formattedRenewalDate}</small>
              </div>
              <div className="text-end">
                <span className="h3 fw-bold text-dark mb-0">{formattedAmount}</span>
                <div className="text-muted small">/ {billingLabel.toLowerCase()}</div>
              </div>
            </div>

            <hr className="my-3 border-secondary border-dashed" style={{ borderStyle: 'dashed' }} />

            
            <div className="small text-secondary mb-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Billing contact</span>
                <span className="fw-semibold text-dark">{organizationName || "-"}</span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span>Billing period</span>
                <span className="fw-semibold text-dark">{billingLabel}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Amount paid</span>
                <span className="fw-semibold text-dark">{formattedAmount}</span>
              </div>
            </div>

            <hr className="my-3" />

           
            <div className="mb-1">
              <div className="text-uppercase text-muted fw-bold mb-2" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                Included in your plan
              </div>
              
              <div className="row g-2 small text-secondary">
                <div className="col-6 d-flex align-items-center gap-2">
                  <MdCheck style={{ color: '#0d9488'}}/>
                  <span>{plan.doctors_limit ?? 0} provider seats</span>
                </div>
                <div className="col-6 d-flex align-items-center gap-2">
                  <MdCheck style={{ color: '#0d9488'}}/>
                  <span>{plan.patients_limit ?? 0} patient records</span>
                </div>
                <div className="col-6 d-flex align-items-center gap-2">
                  <MdCheck style={{ color: '#0d9488'}}/>
                  <span>{plan.storage_size ?? 0} GB storage</span>
                </div>
                {/* <div className="col-6 d-flex align-items-center gap-2">
                  <MdCheck style={{ color: '#0d9488'}}/>
                  <span>Care AI & Coding AI</span>
                </div>
                <div className="col-6 d-flex align-items-center gap-2">
                  <MdCheck style={{ color: '#0d9488'}}/>
                  <span>Priority support</span>
                </div>
                <div className="col-6 d-flex align-items-center gap-2">
                  <MdCheck style={{ color: '#0d9488'}}/>
                  <span>Signed HIPAA BAA</span>
                </div> */}
              </div>
            </div>

          </div>

          
          <div className="d-flex justify-content-between align-items-center pt-2">
            <Link
                  to="/"
                  className="btn btn-outline-secondary rounded-pill btn-sm px-3 fw-semibold"
                >
                  Back to Login
                </Link>
            {/* <button className="btn btn-outline-secondary rounded-pill btn-sm px-3 fw-semibold">
              Back to login
            </button> */}
            <span className="text-muted small">
              Redirecting to login in <strong className="text-primary">{countdown}s</strong>
            </span>
          </div>

        </div>
      </div>
    </div>


  
    </Fragment>


    
  );
};

export default SubscriptionSuccess;